import { esarVhpIdSequenceName } from '@juvare/credentialing/backend/config/shared';
import { administratorAccountEmail } from '@juvare/credentialing/backend/constants';
import { defineEntity } from '@juvare/platform-app/App';
import { AppState, UserState } from '@juvare/platform-sdk-react/types';
import { createHistory } from '../../background-tasks';
import { BackgroundCheckStatus, BackgroundCheckType } from '../../types';
import { generateEsarVhpId } from '../../utils';
import {
    BackgroundCheckHistoryColumns,
    BackgroundCheckHistoryData,
    backgroundCheckHistoryEntityName,
} from '../background-check-history-data';
import { BackgroundCheckColumns, BackgroundCheckData, backgroundCheckColumns } from './types';
import { recalculateBackgroundCheckStatus } from './utils';

export const backgroundCheckEntity = defineEntity<BackgroundCheckData, BackgroundCheckColumns, AppState, UserState>({
    version: 4,
    columns: backgroundCheckColumns,
    onMigrate: (params) => params.oldData,
    onChangedAsync: (context, params) => {
        if (params.action === 'update' && params.data.systemStatus === 'deleted') {
            try {
                const historyStorage = context.storage<BackgroundCheckHistoryData, BackgroundCheckHistoryColumns>(
                    backgroundCheckHistoryEntityName,
                );

                const historyRows = historyStorage.list({
                    filter: [
                        {
                            column: 'checkResponseByRequestId',
                            op: 'is-not-null',
                        },
                        {
                            column: 'accountId',
                            op: '=',
                            value: params.id,
                        },
                    ],
                }).rows;

                if (historyRows.length !== 0) {
                    historyRows.forEach((historyRow) => {
                        createHistory({
                            historyStorage,
                            type: params.data.primaryBackgroundCheckType as BackgroundCheckType,
                            accountId: historyRow.columns.accountId,
                            accountData: params.data,
                            nextStatus: 'processingError',
                            message: 'User deleted',
                            stopCheckingForResultsHistoryId: historyRow.id,
                        });
                    });
                }
            } catch (e) {
                console.log('Unknown error occurred while updating history to stop background check task');
            }
        }
    },
    onAuthPost: (appState, userState, params) => {
        // During client migration, we need to bypass standard authorization checks to manage data as needed.
        // This is only permitted for the administrator account.
        if (userState.email === administratorAccountEmail) {
            return params.action === 'delete' ? params.prevData : params.data;
        }

        if (params.action === 'create' || params.action === 'update') {
            delete params.data.internalBackgroundCheckChange;
        }

        // esarVhpId is not changeable
        if (params.action === 'update' && params.data.esarVhpId !== params.prevData.esarVhpId) {
            params.data.esarVhpId = params.prevData.esarVhpId;
        }

        // users cannot set esarVhpId through UI. Only administrator account can bypass this check during client migration.
        if (params.action === 'create') {
            params.data.esarVhpId = undefined;
        }

        // we only change status through platform function, so no need to check permissions
        if (params.action === 'update') {
            params.data.primaryBackgroundCheckType = params.prevData.primaryBackgroundCheckType;
            params.data.primaryBackgroundCheckStatus = params.prevData.primaryBackgroundCheckStatus;
        }
        if (params.action === 'create' || params.action === 'update') {
            return params.data;
        }
        return;
    },
    onTransform: (context, params) => {
        let esarVhpId;

        if (params.action === 'create' && !params.data.esarVhpId) {
            const sequence = context.sequence(esarVhpIdSequenceName).getNextValue();
            const esarVhp = context.appState.settings.esarVhp;
            esarVhpId = generateEsarVhpId({
                clientCode: esarVhp.clientCode,
                sequence: sequence,
            });
            params.data.esarVhpId = esarVhpId;
        } else {
            esarVhpId = params.data.esarVhpId;
        }

        const internalChange = params.data.internalBackgroundCheckChange;

        if (params.action === 'create' && !internalChange) {
            const backgroundCheckData = recalculateBackgroundCheckStatus(context, params);
            params.data = { ...params.data, ...backgroundCheckData };
        }

        if (internalChange) {
            if (internalChange.isManualChange) {
                params.data.primaryBackgroundCheckType = internalChange.type;
                params.data.primaryBackgroundCheckStatus = internalChange.status;
                params.data.primaryBackgroundUpdatedAt = internalChange.updatedAt;
            } else if (params.data.primaryBackgroundCheckType === internalChange.type) {
                params.data.primaryBackgroundCheckStatus = internalChange.status;
                params.data.primaryBackgroundUpdatedAt = internalChange.updatedAt;
            }
            params.data.backgroundCheck = {
                ...params.data.backgroundCheck,
                [internalChange.type]: {
                    status: internalChange.status,
                    updatedAt: internalChange.updatedAt,
                },
            };
            delete params.data.internalBackgroundCheckChange;
        } else {
            if (params.action === 'update') {
                const handleStatusList: Array<BackgroundCheckStatus> = ['didNotConsent', 'formIncomplete', 'readyToSubmit'];

                if (
                    handleStatusList.includes(params.data.primaryBackgroundCheckStatus as BackgroundCheckStatus) ||
                    params.prevData.consentBackgroundCheck !== params.data.consentBackgroundCheck
                ) {
                    const backgroundCheckData = recalculateBackgroundCheckStatus(context, params);

                    params.data = { ...params.data, ...backgroundCheckData };
                }
            }
        }

        return {
            data: params.data,
            columns: {
                esarVhpId,
                backgroundCheckTypeStatus: params.data.backgroundCheck
                    ? Object.entries(params.data.backgroundCheck)
                          .filter(([backgroundCheck]) => !!backgroundCheck)
                          .map(([type, backgroundCheck]) => type + '_' + backgroundCheck?.status)
                    : [],
                primaryBackgroundCheckStatus: params.data.primaryBackgroundCheckStatus,
                primaryBackgroundCheckType: params.data.primaryBackgroundCheckType,
                primaryBackgroundUpdatedAt: params.data.primaryBackgroundUpdatedAt,
                stateOfWork: params.data.stateOfWork,
                consentBackgroundCheck: params.data.consentBackgroundCheck,
            },
        };
    },
});
